/**
 * ColorPickerGrid  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { CustomPicker, InjectedColorProps } from "react-color";
import { ExportedColorProps } from "react-color/lib/components/common/ColorWrap";
import { Alpha } from "react-color/lib/components/common";
import { BEM } from "@adapptio/lib-components";
import { getColorUtils } from "./colorUtils";
import { ColorPickerGridTile } from "./ColorPickerGridTile";
import { colorGridData } from "./colorGridData";
import { ColorPickerHandle } from "./ColorPickerHandle";

export interface IColorPickerGridBaseComponentProps extends InjectedColorProps {
	className?: string;
}

export const ColorPickerGridBaseComponent: React.FC<IColorPickerGridBaseComponentProps> = ({
	className,
	onChange,
	hex,
	...props
}) => {
	const baseClass = BEM("edt-color-picker-grid");

	const getClickHandler = useCallback<(color: string) => () => void>(
		(color) => () => {
			onChange(color);
		},
		[ onChange ]
	);

	const isSelected = useCallback<(color: string) => boolean>(
		(color) => getColorUtils(color).toHexString() === getColorUtils(hex).toHexString(),
		[ hex ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("grid").toClassName()}>
				{colorGridData.map((colorRow, rowIndex) => (
					<div key={`picker-grid-${rowIndex}`} className={baseClass.element("row").toClassName()}>
						{colorRow.map((color, colorIndex) => (
							<ColorPickerGridTile
								key={`picker-grid-${rowIndex}-${colorIndex}`}
								value={color}
								onClick={getClickHandler(color)}
								selected={isSelected(color)}
							/>
						))}
					</div>
				))}
			</div>
			<div className={baseClass.element("alpha").toClassName()}>
				<Alpha onChange={onChange} pointer={ColorPickerHandle} {...props} />
			</div>
		</div>
	);
};

export interface IColoColorPickerGridBaseProps extends ExportedColorProps {
	className?: string;
}

export const ColorPickerGridBase: React.ComponentClass<IColoColorPickerGridBaseProps> = CustomPicker(
	ColorPickerGridBaseComponent
);
